import React from 'react';
import { DownloadButton, Heatmap, Panel, type HeatmapRow } from '../../../../ui-kit';
import type { LookupGridBlock } from '../../../../types/lookupDashboard';

const whole = (v: number) => v.toLocaleString('en-US');

interface GridPanelProps {
  block: LookupGridBlock;
  loading?: boolean;
  /** Excel, or the grid as a picture drawn on the server, as the campaign heatmap has. */
  onDownload: (format: 'xlsx' | 'png' | 'svg') => void;
  downloading?: boolean;
  /** Problem with this grid's download, shown along its foot. */
  problem?: string | null;
  /** Opens a row — on Planned calls by hour, that hour's clients in the call list. */
  onRowOpen?: (key: string) => void;
}

/**
 * One count for every row and column pair, shaded light to dark: on the Daily
 * Call Plan, how many clients we reached on each weekday in each time window.
 * Each cell's hover says the pair and what it counts in words.
 */
export default function GridPanel({ block, loading, onDownload, downloading, problem, onRowOpen }: GridPanelProps) {
  const columnLabel = React.useMemo(
    () => new Map(block.columns.map((c) => [c.key, c.label])),
    [block.columns],
  );
  const counts = block.cell_label ? ` ${block.cell_label}` : '';
  const totals = block.totals ?? null;
  const rows: HeatmapRow[] = block.rows.map((r, i) => ({
    key: r.key,
    label: r.label,
    cells: r.cells.map((c) => ({
      key: c.key,
      value: c.value,
      title: `${columnLabel.get(c.key) ?? c.key} · ${r.label} · ${whole(c.value)}${counts}`,
    })),
    summaries: totals
      ? [{ key: 'total', value: totals.rows[i] ?? 0, title: `${r.label} on any day · ${whole(totals.rows[i] ?? 0)}${counts}, each once` }]
      : undefined,
  }));
  // Worked out on the server, each client once: a client reached at two hours
  // sits in two cells but counts once here, so these are not the cells added up.
  const totalRow = totals
    ? {
      label: totals.label,
      summaries: [{ key: 'total', value: totals.all, title: `Any day and hour · ${whole(totals.all)}${counts}` }],
      cells: totals.columns.map((c) => ({
        key: c.key,
        value: c.value,
        title: `${columnLabel.get(c.key) ?? c.key} at any hour · ${whole(c.value)}${counts}, each once`,
      })),
    }
    : undefined;

  // Few columns (the weekdays), so they share the panel's full width.
  const grid = () => (
    <Heatmap
      ariaLabel={block.title ?? 'Grid'}
      rowLabel={block.row_label ?? undefined}
      columns={block.columns}
      rows={rows}
      formatValue={whole}
      cellEmptyText="0"
      summaryColumns={totals ? [{ key: 'total', label: totals.label }] : undefined}
      totalRow={totalRow}
      stretch
      onRowOpen={onRowOpen}
      rowOpenLabel={onRowOpen ? 'in the call list' : undefined}
    />
  );

  return (
    <Panel
      title={block.title}
      subtitle={block.subtitle}
      actions={
        <DownloadButton
          formats={[
            { key: 'xlsx', label: 'Excel (.xlsx)', description: 'Every cell, to work with', onDownload: () => onDownload('xlsx') },
            { key: 'png', label: 'Image (.png)', description: 'The grid as it stands, to paste into a deck or an email', onDownload: () => onDownload('png') },
            { key: 'svg', label: 'Vector image (.svg)', description: 'The same picture, sharp at any size', onDownload: () => onDownload('svg') },
          ]}
          tooltip="Download this grid"
          loading={downloading}
          disabled={loading || rows.length === 0}
        />
      }
      loading={loading}
      loadingHeight={280}
      footer={problem ?? null}
      expanded={{ content: grid() }}
    >
      {grid()}
    </Panel>
  );
}
